import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import store from "@/store";
import router from "@/router";

// TODO: exclude other base urls and registration endpoints, otherwise the token will leak
type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

type RequestQueueEntry = {
  resolve: (token: string) => void;
  reject: (error?: unknown) => void;
};

const setAuthorizationHeader = (
  requestConfig: RetryableRequestConfig,
  token: string,
) => {
  requestConfig.headers = requestConfig.headers || new AxiosHeaders();
  requestConfig.headers.Authorization = "Bearer " + token;
};

export const AuthInterceptor = (config: InternalAxiosRequestConfig) => {
  if (!store.getters.token) {
    return config;
  }
  const token = store.getters.token.token;
  if (token != null) {
    setAuthorizationHeader(config, token);
  }
  return config;
};

let isRefreshing = false;
let requestQueue: RequestQueueEntry[] = [];

const LOGIN_URL = "/services/api/v2/general/login";
const REFRESH_URL = "/services/api/v2/general/renew-token/";
const LEGACY_LOGIN = "/auth/token";
const LEGACY_REFRESH = "/auth/token/refresh";

export const AuthErrorInterceptor = (error: AxiosError) => {
  const originalRequest = error.config as RetryableRequestConfig | undefined;
  const isLoginRequest =
    originalRequest?.url?.includes(LOGIN_URL) ||
    originalRequest?.url?.includes(LEGACY_LOGIN);
  const isRefreshRequest =
    originalRequest?.url?.includes(REFRESH_URL) ||
    originalRequest?.url?.includes(LEGACY_REFRESH);
  const responseData = error.response?.data as { error?: string } | undefined;

  if (isRefreshRequest) {
    store.dispatch("logout");
    return Promise.reject(error);
  }

  // Handle 403 Forbidden errors - redirect to homepage unless already there
  // Skip hardware restriction key_required errors (handled by hardwareRestrictionInterceptor)
  if (
    error.response !== undefined &&
    error.response.status === 403 &&
    responseData?.error !== "key_required" &&
    router.currentRoute.value.path !== "/"
  ) {
    void router.push("/");
    return Promise.reject(error);
  }

  if (
    error.response !== undefined &&
    error.response.status !== undefined &&
    error.response.status === 401 &&
    !originalRequest?._retry &&
    !isLoginRequest &&
    !isRefreshRequest
  ) {
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>(function (resolve, reject) {
        requestQueue.push({ resolve, reject });
      })
        .then((token: string) => {
          setAuthorizationHeader(originalRequest, token);
          return axios(originalRequest);
        })
        .catch((err) => {
          return err;
        });
    }

    if (store.getters.token === undefined) {
      store.dispatch("logout");
    }

    originalRequest._retry = true;
    isRefreshing = true;

    return new Promise(function (resolve, reject) {
      store
        .dispatch("refreshToken")
        .then((response: AxiosResponse) => {
          const data = response.data;
          setAuthorizationHeader(originalRequest, data.token);
          processQueue(null, data.token);
          resolve(axios(originalRequest));
        })
        .catch((err: Error) => {
          processQueue(err, null);
          reject(err);
          console.log(err);
        })
        .finally(() => {
          isRefreshing = false;
        });
    });
  }
  return Promise.reject(error);
};

const processQueue = (error: Error | null, token: string | null) => {
  requestQueue.forEach((prom) => {
    if (error !== null || token === null) {
      prom.reject();
    } else {
      prom.resolve(token);
    }
  });
  requestQueue = [];
};
